import { ShopConfig, ShopSeoConfig } from '../data/shopConfig'

/**
 * Whether the shop is presentable on Google — expressed as ready or not, never
 * as a score. Anything beyond a title, a description and being visible is a
 * genuinely optional nicety, so it must not make a merchant feel behind.
 */
export interface SeoStatus {
  ready: boolean
  /** What is actually missing before the shop can be found at all. */
  blocking: string[]
  /** Nice-to-haves. These never affect `ready`. */
  optional: string[]
}

export function seoStatus(seo: ShopSeoConfig): SeoStatus {
  const blocking: string[] = []
  if (seo.title.trim().length < 8) blocking.push('Add a shop title')
  if (seo.description.trim().length < 40)
    blocking.push('Add a short description')
  if (!seo.indexable) blocking.push('Turn on Google visibility')

  const optional: string[] = []
  if (!seo.shareImage) optional.push('Add a sharing image')

  return {
    ready: blocking.length === 0,
    blocking,
    optional,
  }
}

/** One plain line for the My Shop card. */
export function seoStatusLabel(status: SeoStatus): string {
  if (!status.ready) return status.blocking[0]
  if (status.optional.length === 0) return 'Ready for search'
  return `${status.optional.length} optional improvement${status.optional.length > 1 ? 's' : ''}`
}

/** What Talab would write, from the shop profile it already holds. */
export function suggestSeo(config: ShopConfig): {
  title: string
  description: string
  topics: string[]
} {
  const name = config.publicName || 'Our shop'
  const kind = config.businessType || 'Business'
  const cities = config.locations
    .map((l) => l.address.split(',').pop()?.trim())
    .filter(Boolean) as string[]
  const where =
    cities.length > 1
      ? `${cities.slice(0, -1).join(', ')} and ${cities[cities.length - 1]}`
      : (cities[0] ?? config.countries[0] ?? 'the UAE')
  const region =
    config.countries[0] === 'United Arab Emirates'
      ? 'UAE'
      : (config.countries[0] ?? where)
  const cats = config.content.categories
    .filter((c) => c.visible)
    .map((c) => c.name)

  const lead = cats.slice(0, 3).join(', ').toLowerCase()
  return {
    title: `${name} | ${kind} in ${region}`,
    description: `${lead ? `Browse ${lead} from ` : ''}${name} across ${where}. Ask questions, compare options and get answers instantly.`,
    topics: [
      `${kind.toLowerCase()} ${cities[0] ?? where}`,
      ...cats.slice(0, 3).map((c) => `${c.toLowerCase()} ${cities[0] ?? where}`),
    ].slice(0, 4),
  }
}
